import { useMemo, useState, useEffect } from 'react'

import {
  MRT_ToggleDensePaddingButton,
  MRT_ShowHideColumnsButton,
  MRT_ToggleFullScreenButton,
  MRT_ToggleFiltersButton,
  MRT_ToggleGlobalFilterButton
} from 'material-react-table'

import moment from 'moment-timezone'
import _ from 'lodash'
import SaveAltIcon from '@mui/icons-material/SaveAlt'
import RoomOutlinedIcon from '@mui/icons-material/RoomOutlined'

import { Box, Tooltip, IconButton } from '@mui/material'
import MapPopup from '/components/passes/map-popup'
import StudioTable, { useStudioTable } from '/components/studio-table'
import DataExportDialog from "../data-export-dialog"
import excelExportGift from './daily-scan-report-to-excel-gift'
import excelExportLoyalty from './daily-scan-report-to-excel-loyalty'
import excelExportStamp from './daily-scan-report-to-excel-stamp'
import excelExportEvent from './daily-scan-report-to-excel-event'

const ScanReportScans = ({ templateId, pass_type_id, scans, loading }) => {

  const [scan, setScan] = useState()
  const [mapOpen, setMapOpen] = useState(false)
  const [dataExportDialogOpen, setDataExportDialogOpen] = useState(false)

  const excelExport = useMemo(() => {
    switch (pass_type_id) {
      case "managed_gift": return excelExportGift
      case "managed_loyalty": return excelExportLoyalty
      case "managed_stamp": return excelExportStamp
      default: return excelExportEvent
    }
  }, [pass_type_id])

  const openMap = scan => {
    setScan(scan)
    setMapOpen(true)
  }

  const columns = useMemo(() => {
    let cols = [
      {
        id: "timestamp",
        accessorFn: (originalRow) => moment(originalRow.timestamp, "YYYYMMDDHHmmSS"),
        Cell: ({ cell, row }) => cell.getValue().format("DD MMM YYYY HH:mm"),
        header: 'Date',
        filterVariant: 'date-range',
        sortingFn: 'datetime',
        size: 180
      },
      {
        accessorKey: 'pass_id',
        header: 'Pass_id',
        filterVariant: 'autocomplete',
        size: 100,
      },
      {
        accessorKey: 'action',
        header: 'Action',
        filterVariant: 'autocomplete',
        size: 100,
      },
    ]

    if (pass_type_id) switch (pass_type_id) {
      case "managed_loyalty": {
        cols = cols.concat([
          {
            accessorKey: 'points_added',
            header: 'Points Added',
            filterVariant: 'autocomplete',
            size: 100,
          },
          {
            accessorKey: 'points_redeemed',
            header: 'Points Redeemed',
            filterVariant: 'autocomplete',
            size: 100,
          },
        ])
        break
      }

      case "managed_stamp": {
        cols = cols.concat([
          {
            accessorKey: 'stamps',
            header: 'Stamps Added',
            filterVariant: 'autocomplete',
            size: 100,
          },
          {
            accessorKey: 'cards',
            header: 'Cards Redeemed',
            filterVariant: 'autocomplete',
            size: 100,
          },
          {
            id: "admin_adjustment",
            accessorFn: (originalRow) => originalRow.admin,
            header: "Admin Adjustment",
            filterVariant: 'select',
            filterFn: 'equals',
            filterSelectOptions: [
              { label: 'Yes', value: true },
              { label: 'No', value: false },
            ],
            size: 200,
            Cell: ({ cell }) => cell.getValue() ? <AutoAwesomeOutlinedIcon /> : null
          },
        ])
        break
      }

      case "managed_gift": {
        cols = cols.concat([
          {
            accessorKey: 'amount_debited',
            header: 'Debits',
            filterVariant: 'range',
            filterFn: 'between',
            size: 20,
            Cell: ({ cell }) => moneyFormat("debit", cell.getValue()),
            muiTableBodyCellProps: {
              align: 'right',
            },
            muiTableFooterCellProps: {
              align: 'right',
            },
          },
          {
            accessorKey: 'amount_credited',
            header: 'Refunds',
            filterVariant: 'range',
            filterFn: 'between',
            size: 20,
            Cell: ({ cell }) => moneyFormat("refund", cell.getValue()),
            muiTableBodyCellProps: {
              align: 'right',
            },
            muiTableFooterCellProps: {
              align: 'right',
            },
          },
          {
            accessorKey: 'amount_topped_up',
            header: 'Topups',
            filterVariant: 'range',
            filterFn: 'between',
            size: 20,
            Cell: ({ cell }) => moneyFormat("topup", cell.getValue()),

            muiTableBodyCellProps: {
              align: 'right',
            },
            muiTableFooterCellProps: {
              align: 'right',
            },
          },
        ])
        break
      }

      default: { // managed_event / event_bg
        cols = cols.concat([
          {
            accessorKey: 'error',
            header: 'Error',
            filterVariant: 'autocomplete',
            size: 200,
          },
        ])
      }
    }
    
    cols = cols.concat([
      {
        accessorKey: 'username',
        header: 'Scanned by',
        filterVariant: 'autocomplete',
        size: 100,
      },
      {
        accessorKey: 'reference',
        header: 'Reference',
        filterVariant: 'autocomplete',
        size: 100,
      },
      {
        accessorKey: 'note',
        header: 'Note',
        filterVariant: 'autocomplete',
        size: 100,
      },
      {
        id: 'position',
        accessorFn: (originalRow) => originalRow,
        header: 'Scan Location',
        filterVariant: 'autocomplete',
        size: 400,
        Cell: ({ cell }) => {
          const row = cell.getValue()
          return row.position ? <div className='link' onClick={() => openMap(row)}><RoomOutlinedIcon className='mr-5' />{row.position.x},{row.position.y}</div> : null
        }
      },
    ])


    return cols
  }, [pass_type_id])



  const table = useStudioTable({
    tableKey: 'daily-scan-report-scans',
    columns,
    data: scans,
    state: {
      isLoading: loading
    },
    initialState: { showColumnFilters: false },
    enablePagination: false,
    enableBottomToolbar: false,
    enableRowSelection: false,
    enableMultiRowSelection: false,
    enableRowActions: false,
    enableStickyHeader: true,
    enableStickyFooter: true,
    positionToolbarAlertBanner: "none",
    muiTablePaperProps: {
      sx: {
        top: 180,
        bottom: 5,
        left: 5,
        right: 5,
        position: "fixed"
      },
      elevation: 3,
    },
    muiTableContainerProps: {
      sx: {
        top: 220,
        bottom: 5,
        left: 5,
        right: 5,
        position: "fixed"
      }
    },
    renderToolbarInternalActions: ({ table }) => (
      <Box>
        <MRT_ToggleGlobalFilterButton table={table} />
        <MRT_ToggleFiltersButton table={table} />
        <MRT_ShowHideColumnsButton table={table} />
        <MRT_ToggleDensePaddingButton table={table} />
        <MRT_ToggleFullScreenButton table={table} />
        <Tooltip title="Export"><IconButton onClick={() => setDataExportDialogOpen(true)}><SaveAltIcon /></IconButton></Tooltip>
      </Box>
    )
  })

  return (
    <>
      <StudioTable table={table} />
      <MapPopup scan={scan} open={mapOpen} setOpen={setMapOpen} />
      <DataExportDialog open={dataExportDialogOpen} setOpen={setDataExportDialogOpen} fileName={`${templateId}-daily-scan-report`} data={scans} excelExport={excelExport} table={table} />
    </>)
}

export default ScanReportScans

const moneyFormat = (action, value) => {
  switch (action) {
    case "debit": return value ? <span className="red">£{value?.toFixed(2)}</span> : ""
    case "refund": return value ? <span className="green">£{value?.toFixed(2)}</span> : ""
    case "topup": return value ? <span className="green">£{value?.toFixed(2)}</span> : ""

    default: return ""
  }
}
